import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Container,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { 
  Send, 
  ContentCopy, 
  Delete, 
  PersonAdd, 
  Email,
  Share,
  CheckCircle,
  Cancel,
  Schedule
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { 
  useCreateInvitation, 
  useInvitations, 
  useCancelInvitation, 
  useCopyInviteUrl,
  useSendInvitationEmail
} from '../hooks/useInvitations';

const AdminInvitationManagement: React.FC = () => {
  const { organization } = useAuth();
  const [inviteEmail, setInviteEmail] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelInvitationData, setCancelInvitationData] = useState<{ id: string; email: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Hooks
  const createInvitationMutation = useCreateInvitation();
  const { data: invitations = [], isLoading: invitationsLoading, error: invitationsError } = useInvitations();
  const cancelInvitationMutation = useCancelInvitation();
  const { copyInviteUrl } = useCopyInviteUrl();
  const sendEmailMutation = useSendInvitationEmail();

  const handleCreateInvitation = async () => {
    if (!inviteEmail.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }

    if (!inviteEmail.includes('@')) {
      setError('正しいメールアドレスを入力してください');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createInvitationMutation.mutateAsync(inviteEmail.trim());
      
      setSuccess(`招待を作成しました: ${inviteEmail}`);
      setInviteEmail('');
      setShowInviteDialog(false);
      
      // クリップボードに招待URLをコピー
      if (result.token) {
        await copyInviteUrl(result.token);
        setSuccess(prev => prev + '\n招待URLをクリップボードにコピーしました');
      }
      
      // 成功メッセージを3秒後に消す
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || '招待の作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitationClick = (invitationId: string, email: string) => {
    setCancelInvitationData({ id: invitationId, email });
    setShowCancelDialog(true);
  };

  const handleCancelInvitation = async () => {
    if (!cancelInvitationData) return;

    try {
      await cancelInvitationMutation.mutateAsync(cancelInvitationData.id);
      setSuccess('招待をキャンセルしました');
      setTimeout(() => setSuccess(null), 3000);
      setShowCancelDialog(false);
      setCancelInvitationData(null);
    } catch (err: any) {
      setError(err.message || '招待のキャンセルに失敗しました');
    }
  };

  const handleCopyInviteUrl = async (token: string) => {
    try {
      const copied = await copyInviteUrl(token);
      if (copied) {
        setSuccess('招待URLをクリップボードにコピーしました');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('コピーに失敗しました');
      }
    } catch (err: any) {
      setError('コピーに失敗しました');
    }
  };

  const handleSendEmail = async (email: string, token: string) => {
    try {
      // プレースホルダー実装（メール送信は将来実装）
      await handleCopyInviteUrl(token);
      setSuccess(`${email} 用の招待URLをクリップボードにコピーしました\n招待したい方にURLを共有してください`);
    } catch (err: any) {
      setError('招待URL送信に失敗しました');
    }
  };

  const getStatusChip = (invitation: any) => {
    const now = new Date();
    const expiresAt = new Date(invitation.expires_at);
    const isExpired = now > expiresAt;
    
    if (invitation.used_at) {
      return <Chip label="受諾済み" color="success" size="small" icon={<CheckCircle />} />;
    }
    
    if (!(invitation as any).is_active) {
      return <Chip label="キャンセル済み" color="default" size="small" icon={<Cancel />} />;
    }
    
    if (isExpired) {
      return <Chip label="期限切れ" color="error" size="small" icon={<Schedule />} />;
    }
    
    return <Chip label="有効" color="primary" size="small" icon={<CheckCircle />} />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            管理者招待
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {organization?.name || 'マンション管理組合'} の管理者を招待
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setShowInviteDialog(true)}
        >
          新規招待
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Alert>
      )}

      {/* 招待一覧テーブル */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            招待一覧
          </Typography>
          
          {invitationsLoading ? (
            <Typography>読み込み中...</Typography>
          ) : invitationsError ? (
            <Alert severity="error">
              招待一覧の取得に失敗しました
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>招待先</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>作成日時</TableCell>
                    <TableCell>有効期限</TableCell>
                    <TableCell align="center">アクション</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          招待はありません
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Email sx={{ mr: 1, color: 'text.secondary' }} />
                            {(invitation as any).email}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(invitation)}
                        </TableCell>
                        <TableCell>
                          {formatDate(invitation.created_at)}
                        </TableCell>
                        <TableCell>
                          {formatDate(invitation.expires_at)}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {(invitation as any).is_active && !invitation.used_at && (
                              <>
                                <Tooltip title="招待URLをコピー">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyInviteUrl(invitation.token)}
                                  >
                                    <ContentCopy />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="招待をキャンセル">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleCancelInvitationClick(invitation.id, (invitation as any).email)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 招待作成ダイアログ */}
      <Dialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>管理者を招待</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            新しい管理者のメールアドレスを入力してください。
            招待URLが生成され、クリップボードにコピーされます。
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="メールアドレス"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="admin@example.com"
            error={!!error}
            helperText={error}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateInvitation();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInviteDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateInvitation}
            disabled={loading}
            startIcon={<Send />}
          >
            {loading ? '作成中...' : '招待を作成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 招待キャンセル確認ダイアログ */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>招待キャンセルの確認</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {cancelInvitationData?.email} への招待をキャンセルしますか？
          </Typography>
          <Typography variant="body2" color="text.secondary">
            この操作は取り消すことができません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelInvitation}
            disabled={cancelInvitationMutation.isPending}
            startIcon={<Delete />}
          >
            {cancelInvitationMutation.isPending ? 'キャンセル中...' : '招待をキャンセル'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminInvitationManagement;